(() => {
  function clamp(n, min, max) {
    return Math.max(min, Math.min(n, max));
  }

  function dist(t1, t2) {
    const dx = t2.clientX - t1.clientX;
    const dy = t2.clientY - t1.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  function initGallery(root) {
    const modal = root.querySelector(".product-ugc__modal");
    const fullImg = root.querySelector("[data-ugc-full-img]");
    const viewer = root.querySelector("[data-ugc-viewer]");
    const thumbs = Array.from(root.querySelectorAll("[data-ugc-full]"));

    if (!modal || !fullImg || !viewer || thumbs.length === 0) return;

    // ✅ move modal to <body> so fixed positioning isn't trapped by transforms/overflow
    if (!modal.dataset.movedToBody) {
      document.body.appendChild(modal);
      modal.dataset.movedToBody = "1";
    }

    let index = 0;

    // Zoom/pan state
    let scale = 1;
    let minScale = 1;
    let maxScale = 4;
    let tx = 0;
    let ty = 0;

    let isPanning = false;
    let panStartX = 0;
    let panStartY = 0;
    let panStartTx = 0;
    let panStartTy = 0;

    let isPinching = false;
    let pinchStartDist = 0;
    let pinchStartScale = 1;

    let lastTap = 0;

    function applyTransform() {
      fullImg.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`;
      fullImg.style.transformOrigin = "center center";
      fullImg.style.transition = "none";
      fullImg.style.cursor = scale > 1 ? "grab" : "default";
    }

    function resetTransform() {
      scale = 1;
      tx = 0;
      ty = 0;
      applyTransform();
    }

    function setImage(i) {
      index = ((i % thumbs.length) + thumbs.length) % thumbs.length;
      const url = thumbs[index].getAttribute("data-ugc-full");
      fullImg.src = url;
      resetTransform();
    }

    function open(i) {
      setImage(i);

      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");

      document.documentElement.classList.add("ugc-modal-open");
      document.body.classList.add("ugc-modal-open");
    }

    function close() {
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      fullImg.removeAttribute("src");

      document.documentElement.classList.remove("ugc-modal-open");
      document.body.classList.remove("ugc-modal-open");

      resetTransform();
    }

    function next() {
      setImage(index + 1);
    }

    function prev() {
      setImage(index - 1);
    }

    // -----------------------
    // Basic open/close/nav
    // -----------------------
    thumbs.forEach((btn, i) => btn.addEventListener("click", () => open(i)));
    modal.querySelectorAll("[data-ugc-close]").forEach((el) => el.addEventListener("click", close));
    modal.querySelector("[data-ugc-next]")?.addEventListener("click", next);
    modal.querySelector("[data-ugc-prev]")?.addEventListener("click", prev);

    document.addEventListener("keydown", (e) => {
      if (modal.hidden) return;
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    });

    // -----------------------
    // Swipe (only when NOT zoomed)
    // -----------------------
    let swipeStartX = 0;
    let swipeTracking = false;

    // -----------------------
    // Touch handlers for zoom/pan
    // -----------------------
    viewer.addEventListener(
      "touchstart",
      (e) => {
        if (modal.hidden) return;

        // double-tap zoom toggle
        const now = Date.now();
        if (e.touches.length === 1 && now - lastTap < 280) {
          // toggle zoom 1x <-> 2x
          if (scale === 1) scale = 2;
          else scale = 1;

          // reset pan when toggling down
          if (scale === 1) {
            tx = 0;
            ty = 0;
          }
          applyTransform();
          lastTap = 0;
          return;
        }
        lastTap = now;

        if (e.touches.length === 2) {
          // pinch start
          isPinching = true;
          isPanning = false;
          pinchStartDist = dist(e.touches[0], e.touches[1]);
          pinchStartScale = scale;
        } else if (e.touches.length === 1) {
          // if zoomed -> pan, else -> swipe
          if (scale > 1) {
            isPanning = true;
            panStartX = e.touches[0].clientX;
            panStartY = e.touches[0].clientY;
            panStartTx = tx;
            panStartTy = ty;
          } else {
            swipeTracking = true;
            swipeStartX = e.touches[0].clientX;
          }
        }
      },
      { passive: true }
    );

    viewer.addEventListener(
      "touchmove",
      (e) => {
        if (modal.hidden) return;

        // pinch zoom
        if (isPinching && e.touches.length === 2) {
          const d = dist(e.touches[0], e.touches[1]);
          const ratio = d / pinchStartDist;
          scale = clamp(pinchStartScale * ratio, minScale, maxScale);

          // when scale goes back to 1, also reset translate
          if (scale === 1) {
            tx = 0;
            ty = 0;
          }

          applyTransform();
          return;
        }

        // pan (drag) when zoomed
        if (isPanning && e.touches.length === 1 && scale > 1) {
          const x = e.touches[0].clientX;
          const y = e.touches[0].clientY;
          tx = panStartTx + (x - panStartX);
          ty = panStartTy + (y - panStartY);

          // light clamp so it doesn't fly away (simple, not perfect math)
          tx = clamp(tx, -window.innerWidth, window.innerWidth);
          ty = clamp(ty, -window.innerHeight, window.innerHeight);

          applyTransform();
          return;
        }
      },
      { passive: true }
    );

    viewer.addEventListener(
      "touchend",
      (e) => {
        if (modal.hidden) return;

        // pinch end
        if (isPinching) {
          isPinching = false;
          if (scale === 1) {
            tx = 0;
            ty = 0;
            applyTransform();
          }
          return;
        }

        // swipe end (only if not zoomed)
        if (swipeTracking && scale === 1) {
          swipeTracking = false;
          const endX = e.changedTouches[0].clientX;
          const dx = endX - swipeStartX;
          if (Math.abs(dx) < 40) return;
          dx < 0 ? next() : prev();
        }

        // pan end
        isPanning = false;
      },
      { passive: true }
    );

    // Optional: mouse drag pan on desktop when zoomed
    viewer.addEventListener("mousedown", (e) => {
      if (modal.hidden) return;
      if (scale <= 1) return;

      isPanning = true;
      panStartX = e.clientX;
      panStartY = e.clientY;
      panStartTx = tx;
      panStartTy = ty;
      fullImg.style.cursor = "grabbing";
    });

    window.addEventListener("mousemove", (e) => {
      if (!isPanning || modal.hidden) return;
      if (scale <= 1) return;

      tx = panStartTx + (e.clientX - panStartX);
      ty = panStartTy + (e.clientY - panStartY);
      tx = clamp(tx, -window.innerWidth, window.innerWidth);
      ty = clamp(ty, -window.innerHeight, window.innerHeight);
      applyTransform();
    });

    window.addEventListener("mouseup", () => {
      if (!isPanning) return;
      isPanning = false;
      fullImg.style.cursor = scale > 1 ? "grab" : "default";
    });
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("[data-ugc-gallery]").forEach(initGallery);
  });
})();